import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/shared/status-badge";
import { Badge } from "@/components/ui/badge";
import { formatRelativeTime } from "@/lib/format";
import type { Ticket } from "@db/schema";

interface TicketListItemProps {
  ticket: Ticket;
  isSelected: boolean;
  onClick: () => void;
}

export function TicketListItem({
  ticket,
  isSelected,
  onClick,
}: TicketListItemProps) {
  const activityAt = ticket.lastInboundReplyAt ?? ticket.createdAt;

  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-1 border-b p-4 text-left text-sm w-full transition-colors",
        isSelected
          ? "bg-muted"
          : "hover:bg-accent/50",
      )}
    >
      {/* Row 1: Key + timestamp */}
      <div className="flex w-full items-center gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {ticket.status === "new" && (
            <span className="flex h-2 w-2 shrink-0 rounded-full bg-blue-600" />
          )}
          <span className="font-mono text-xs text-muted-foreground">
            {ticket.key ?? "---"}
          </span>
          {ticket.hasNewReply && (
            <Badge className="h-5 rounded-full bg-amber-100 px-2 text-[10px] font-medium text-amber-800 hover:bg-amber-100">
              New reply
            </Badge>
          )}
        </div>
        <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
          {formatRelativeTime(activityAt)}
        </span>
      </div>

      {/* Row 2: Subject */}
      <p className="font-medium leading-snug truncate w-full">
        {ticket.subject ?? "No subject"}
      </p>

      {/* Row 3: Badges */}
      <div className="flex items-center gap-1.5 mt-0.5">
        <StatusBadge type="priority" status={ticket.priority} />
        <StatusBadge type="ticket" status={ticket.status} />
      </div>
    </button>
  );
}
